import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/fire-debris-removal-page.module.css'
// eslint-disable-next-line import/extensions
import fireDebris from '../../json/fire-debris-removal-data.json'

const fireDebrisServiceData = {
  name: 'Fire Debris Removal',
  description:
    'Fire debris removal and fire damage cleanup for homes and businesses across California. Dimension clears burnt contents, structural debris, ash, and hazardous residue with full documentation and compliant disposal.',
  serviceType: 'Fire Debris Removal and Cleanup',
  url: 'https://getdimension.com/fire-debris-removal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Fire Damage Cleanup' },
    { name: 'Ash and Hazardous Residue Removal' },
    { name: 'Structural Fire Debris Removal' },
    { name: 'Fire-Damaged Contents Removal' },
    { name: 'Wildfire Debris Cleanup' },
  ],
}

const fireDebrisFAQData = {
  questions: fireDebris.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function FireDebrisRemoval() {
  return (
    <>
      <Head>
        <title>Fire Debris Removal &amp; Cleanup in California | Dimension</title>
        <meta
          name="description"
          content="Fire debris removal and fire damage cleanup for California homes and businesses. Dimension clears burnt contents, ash, and hazardous residue safely, with compliant disposal and full documentation."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/fire-debris-removal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={fireDebrisServiceData} />
        <SchemaMarkup type="FAQPage" data={fireDebrisFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="fire-debris"
          /* Southern California only — the heading says so, and we cannot serve a
             headline promising fire debris removal in Houston. */
          disableCityLocalization
          heading={fireDebris.pitch.heading}
          material={fireDebris.pitch.material}
          pitchText={fireDebris.pitch.pitchText}
          ctaLink={fireDebris.pitch.ctaLink}
          ctaText={fireDebris.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...fireDebris.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...fireDebris.benefits} />
        <CaseStudy {...fireDebris.caseStudy} />
        <Approach {...fireDebris.approach} />
        <FAQ {...fireDebris.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Junk Removal Services',
              href: '/junk-removal',
              description:
                'Fast removal of furniture, appliances, and general debris for homes and businesses.',
            },
            {
              label: 'Dumpster Rental',
              href: '/dumpster-rental',
              description:
                'Roll-off dumpsters for cleanouts, renovations, and large-volume debris removal.',
            },
            {
              label: 'Other Hazardous Waste',
              href: '/other-hazardous-waste',
              description:
                'Compliant disposal of toxic, corrosive, flammable, and reactive materials nationwide.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default FireDebrisRemoval
